import { normalize } from 'normalizr';
import { useNavigate } from 'react-router-dom';

import { setUserProperties } from '@sophocles/analytics';
import { receiveCustomers } from '@sophocles/entities/Customer';
import { receiveCustomerOptions } from '@sophocles/entities/CustomerOption';
import { receiveCustomerPreferences } from '@sophocles/entities/CustomerPreference';
import { receiveUsers, userSchema } from '@sophocles/entities/User';
import { receiveUserOptions, userOptionSchema } from '@sophocles/entities/UserOption';
import { addToast } from '@sophocles/features/toast';
// eslint-disable-next-line no-restricted-imports -- pre-existing thunk-level apiClient usage; migrate to api/<domain>.js when touched
import apiClient from '@sophocles/http-common';
import { setLanguage } from '@sophocles/i18n';
import tokenService from '@sophocles/services/token';
import { checkHttpStatus, genericErrorAction } from '@sophocles/utils';

/*
 * General Error Handling
 */

class ApiError {
  constructor(response) {
    this.name = response.statusText;
    this.response = response;
    this.invalidRequest = response.status === 400;
    this.rateLimited = response.status === 429;
    this.notFound = response.status === 404;
    this.notAuthorized = response.status === 403 || response.status === 401;
    this.serverError = response.status === 500;
    if (this.serverError) {
      this.message = response.statusText;
    } else {
      this.message = response.data.detail;
    }
  }

  serialize() {
    return JSON.parse(JSON.stringify(this));
  }
}

const handleErrors = response => {
  if (response.status !== 200) {
    const error = new ApiError(response);
    throw error.serialize();
  }
};

/*
 * New API Requests
 */

async function verifyUsername(username) {
  try {
    const response = await apiClient.get('/auth/verify', { params: { username } });
    return response.data;
  } catch (e) {
    handleErrors(e.response);
  }
}

async function initiateAuth(username, password) {
  try {
    const response = await apiClient.post('/auth/login', {
      username,
      password,
    });
    return response.data;
  } catch (e) {
    handleErrors(e.response);
  }
}

async function performUserImpersonation(userId) {
  try {
    const response = await apiClient.post('/auth/impersonate', { user_id: userId });
    return response.data;
  } catch (e) {
    handleErrors(e.response);
  }
}

async function performTokenRefresh() {
  try {
    const payload = {
      username: tokenService.user.email,
      refresh_token: tokenService.refreshToken,
    };
    if (tokenService.authMethod === 'federated') {
      payload.auth_method = 'federated';
    }
    const response = await apiClient.post('/auth/refresh', payload);
    return response.data;
  } catch (e) {
    tokenService.logout();
  }
}

async function initiatePasswordReset(username, isLegacy) {
  try {
    const response = await apiClient.post('/auth/forgot-password', {
      username,
      legacy: isLegacy,
    });
    return response.data;
  } catch (e) {
    handleErrors(e.response);
  }
}

async function confirmPasswordReset(username, password, code) {
  try {
    const response = await apiClient.post('/auth/forgot-password/confirm', {
      username,
      proposed_password: password,
      code,
    });
    return response.data;
  } catch (e) {
    handleErrors(e.response);
  }
}

async function submitChallenge(challenge, session, username, params) {
  try {
    const response = await apiClient.post('/auth/challenge', {
      username,
      challenge,
      session,
      ...params,
    });
    return response.data;
  } catch (e) {
    handleErrors(e.response);
  }
}

/*
 * New API Actions
 */

export const LOGIN_CHALLENGE_COMPLETE = 'LOGIN_CHALLENGE_COMPLETE';

function loginChallengeComplete() {
  return {
    type: LOGIN_CHALLENGE_COMPLETE,
  };
}

export const CANCEL_LOGIN = 'CANCEL_LOGIN';

export function cancelLogin() {
  return {
    type: CANCEL_LOGIN,
  };
}

export const CANCEL_CHALLENGE = 'CANCEL_CHALLENGE';

export function cancelChallenge() {
  return {
    type: CANCEL_CHALLENGE,
  };
}

export const VERIFY_USER_SUCCESS = 'VERIFY_USER_SUCCESS';

function verifyUserSuccess(pool, id, username, isEnrolled, idps, passwordPolicy) {
  return {
    type: VERIFY_USER_SUCCESS,
    pool,
    id,
    username,
    isEnrolled,
    idps,
    passwordPolicy,
  };
}

export const VERIFY_USER_FAILURE = 'VERIFY_USER_FAILURE';

function verifyUserFailure(err) {
  return {
    type: VERIFY_USER_FAILURE,
    err,
  };
}

export function verifyUserPreAuth(username) {
  return dispatch => {
    return verifyUsername(username)
      .then(data => {
        const idps = [];

        if (Array.isArray(data.idps)) {
          data.idps.forEach(idp => {
            idps.push({
              name: idp.name,
              url: idp.url.replace('callback=', 'redirect_uri='),
            });
          });
        }

        dispatch(verifyUserSuccess(data.pool, data.user, username, data.enrolled, idps, data.password_policy));
        return data;
      })
      .catch(err => {
        dispatch(verifyUserFailure(err));
        return err;
      });
  };
}

export const LOGIN_USER_SUCCESS = 'LOGIN_USER_SUCCESS';

function loginUserSuccess(challenge, tokens) {
  return {
    type: LOGIN_USER_SUCCESS,
    challenge,
    tokens,
  };
}

export const AUTHENTICATION_SUCCESS = 'AUTHENTICATION_SUCCESS';

function authenticationSuccess() {
  return {
    type: AUTHENTICATION_SUCCESS,
  };
}

export const LOGIN_USER_FAILURE = 'LOGIN_USER_FAILURE';

function loginUserFailure(err) {
  return {
    type: LOGIN_USER_FAILURE,
    err,
  };
}

export function loginUser(username, password, legacy = false, remember = false, redirect = '/') {
  return dispatch => {
    dispatch(loginUserRequest());
    if (legacy) {
      return dispatch(fetchAuthTokenLegacy(username, password, redirect, remember)).catch(err =>
        dispatch(loginUserFailure(err)),
      );
    }
    return initiateAuth(username, password)
      .then(data => {
        dispatch(loginUserSuccess(data.challenge, data.tokens));
        if (!data.challenge) {
          return dispatch(fetchAuthUser(redirect, remember));
        }
        return data;
      })
      .catch(err => {
        dispatch(loginUserFailure(err));
        throw err;
      });
  };
}

export function loginChallenge(name, session, username, params, remember = false, redirect = '/') {
  return dispatch => {
    return submitChallenge(name, session, username, params)
      .then(data => {
        dispatch(loginChallengeComplete());
        dispatch(loginUserSuccess(data.challenge, data.tokens));
        if (!data.challenge) {
          return dispatch(fetchAuthUser(redirect, remember));
        }
        return data;
      })
      .catch(err => {
        dispatch(loginUserFailure(err));
      });
  };
}

export const refreshToken = () => {
  return dispatch => {
    return performTokenRefresh()
      .then(data => {
        dispatch(refreshTokenSuccess(data));
      })
      .catch(() => {
        dispatch(logout());
      });
  };
};

export const REFRESH_TOKEN_SUCCESS = 'REFRESH_TOKEN_SUCCESS';

function refreshTokenSuccess(data) {
  return {
    type: REFRESH_TOKEN_SUCCESS,
    idToken: data.tokens.id_token,
    accessToken: data.tokens.access_token,
  };
}

export const PASSWORD_RESET_INITIATE_SUCCESS = 'PASSWORD_RESET_INITIATE_SUCCESS';
function initiateResetPasswordSuccess(codeDelivery, required) {
  return {
    type: PASSWORD_RESET_INITIATE_SUCCESS,
    codeDelivery,
    isRequired: required,
  };
}

export const PASSWORD_RESET_INITIATE_FAILURE = 'PASSWORD_RESET_INITIATE_FAILURE';
function initiateResetPasswordFailure(err) {
  return {
    type: PASSWORD_RESET_INITIATE_FAILURE,
    err,
  };
}

export function resetPassword(username, required, isLegacy) {
  return dispatch => {
    return initiatePasswordReset(username, isLegacy)
      .then(data => {
        if (isLegacy) {
          dispatch(
            addToast({
              title: 'Success!',
              message: 'Password reset e-mail sent!',
              level: 'success',
              dismissible: true,
              autoDismiss: 5,
            }),
          );
          return;
        }
        dispatch(initiateResetPasswordSuccess(data.code_delivery, required));
      })
      .catch(err => {
        dispatch(initiateResetPasswordFailure(err));
      });
  };
}

export const PASSWORD_RESET_SUCCESS = 'PASSWORD_RESET_SUCCESS';
function resetPasswordSuccess() {
  return {
    type: PASSWORD_RESET_SUCCESS,
  };
}

export const PASSWORD_RESET_FAILURE = 'PASSWORD_RESET_FAILURE';
function resetPasswordFailure(err) {
  return {
    type: PASSWORD_RESET_FAILURE,
    err,
  };
}

export function resetPasswordChallenge(username, password, code) {
  return dispatch => {
    return confirmPasswordReset(username, password, code)
      .then(() => {
        dispatch(loginChallengeComplete());
        dispatch(resetPasswordSuccess());
      })
      .catch(err => {
        dispatch(resetPasswordFailure(err));
      });
  };
}

/*
 * Impersonation Actions
 */
export const IMPERSONATE_USER_REQUEST = 'IMPERSONATE_USER_REQUEST';

function impersonateUserRequest() {
  return {
    type: IMPERSONATE_USER_REQUEST,
  };
}

export const IMPERSONATE_USER_SUCCESS = 'IMPERSONATE_USER_SUCCESS';

function impersonateUserSuccess(data) {
  const { user_id, impersonation_token, first_name, last_name, email } = data;
  return {
    type: IMPERSONATE_USER_SUCCESS,
    token: impersonation_token,
    userId: user_id,
    firstName: first_name,
    lastName: last_name,
    email,
  };
}

export const IMPERSONATE_USER_FAILURE = 'IMPERSONATE_USER_FAILURE';

function impersonateUserFailure(err) {
  return {
    type: IMPERSONATE_USER_FAILURE,
    err,
  };
}

export function impersonateUser(userId) {
  return dispatch => {
    dispatch(impersonateUserRequest());
    return performUserImpersonation(userId)
      .then(data => {
        if (!data) {
          dispatch(
            impersonateUserFailure({
              response: {
                status: 404,
                statusText: 'Could not find user?',
              },
            }),
          );
        } else {
          dispatch(impersonateUserSuccess(data));
        }

        return data;
      })
      .catch(error => {
        console.error('error', error);
        dispatch(
          impersonateUserFailure({
            response: {
              status: error.response.status,
              statusText: error.response.data.detail,
            },
          }),
        );

        return error.response.data;
      });
  };
}

/*
 * Legacy API Actions
 */

export const LOGIN_USER_REQUEST = 'LOGIN_USER_REQUEST';

function loginUserRequest() {
  return {
    type: LOGIN_USER_REQUEST,
  };
}

export const LOGOUT_USER = 'LOGOUT_USER';

export function logout() {
  return {
    type: LOGOUT_USER,
  };
}

export function logoutAndRedirect() {
  const navigate = useNavigate();
  return dispatch => {
    dispatch(logout());
    navigate('/login');
  };
}

export function loginUserWithToken(redirect = null) {
  return function (dispatch) {
    dispatch(loginUserRequest());
    return dispatch(fetchAuthUser(redirect));
  };
}

function fetchAuthTokenLegacy(email, password, redirect, remember) {
  return function (dispatch) {
    dispatch(requestAuthToken());
    return apiClient
      .post(`/api-token-auth/`, { username: email.toLowerCase(), password: password })
      .then(checkHttpStatus) // throws error if bad status
      .then(response => response.data) // convert to json
      .then(json => {
        dispatch(loginUserSuccess(null, { id_token: json.token }));
        dispatch(fetchAuthUser(redirect, remember));
      })
      .catch(error => {
        if (error.type === 403) {
          return dispatch(
            loginUserFailure({
              response: {
                status: 403,
                statusText: 'Invalid Email or Password',
              },
            }),
          );
        }

        return dispatch(
          loginUserFailure({
            response: {
              status: 500,
              statusText: 'Internal Server Error',
            },
          }),
        );
      });
  };
}

// Shared promise for an in-flight /users/me/ fetch. Cold boot can trigger more
// than one auth-resolution path before the first request settles (e.g.
// overlapping RequireAuth evaluations across a route change), each dispatching
// fetchAuthUser. Without this guard they each hit /users/me/. Concurrent
// dispatches share one request and one set of store updates; the next dispatch
// after it settles fetches fresh.
let inFlightAuthUserFetch = null;

export function fetchAuthUser(redirect, remember) {
  return dispatch => {
    if (inFlightAuthUserFetch) {
      return inFlightAuthUserFetch;
    }
    dispatch(requestAuthUser());
    inFlightAuthUserFetch = apiClient
      .get(`/users/me/`)
      .then(checkHttpStatus)
      .then(response => {
        if (response.data?.options?.locale) {
          setLanguage(response.data.options.locale);
        } else {
          setLanguage('en-US');
        }
        return normalize(typeof response.data === 'string' ? JSON.parse(response.data) : response.data, userSchema);
      })
      .then(json => {
        setUserProperties(json.entities.users[json.result]);
        dispatch(receiveUsers(json.entities.users));
        dispatch(receiveUserOptions(json.entities.userOptions));
        dispatch(receiveAuthUser(json.result, json.entities.users[json.result].email));
        dispatch(receiveCustomers(json.entities.customers));
        dispatch(receiveCustomerOptions(json.entities.customerOptions));
        dispatch(receiveCustomerPreferences(json.entities.customerPreferences));
        dispatch(authenticationSuccess());
      })
      .catch(error => {
        try {
          if (error.response.data.detail === 'Unknown Pool ID') {
            window.dispatchEvent(new ErrorEvent('error', { message: 'Pool mismatch error', error }));
            tokenService.logout();
            window.location.href = '/login';
          }
        } catch (ex) {
          window.dispatchEvent(new ErrorEvent('error', { message: 'Unknown error validating auth user', error }));
        }
        dispatch(
          loginUserFailure({
            response: {
              status: 403,
              statusText: 'Error Validating Auth Token',
            },
          }),
        );
      })
      .finally(() => {
        // Clear the guard once settled so the next dispatch (e.g. RequireAuth's
        // retry refetch, or a native resume) fetches fresh rather than reusing
        // a resolved promise.
        inFlightAuthUserFetch = null;
      });
    return inFlightAuthUserFetch;
  };
}

export const SET_LOGIN_REDIRECT = 'SET_LOGIN_REDIRECT';

export const REQUEST_AUTH_TOKEN = 'REQUEST_AUTH_TOKEN';

function requestAuthToken() {
  return {
    type: REQUEST_AUTH_TOKEN,
  };
}

export const REQUEST_AUTH_USER = 'REQUEST_AUTH_USER';

function requestAuthUser() {
  return {
    type: REQUEST_AUTH_USER,
  };
}

export const RECEIVE_AUTH_USER = 'RECEIVE_AUTH_USER';

function receiveAuthUser(id, email) {
  return {
    type: RECEIVE_AUTH_USER,
    user: id,
    email: email,
  };
}

export const SET_CUSTOMER = 'SET_CUSTOMER';

export function setCustomer(customer) {
  return {
    type: SET_CUSTOMER,
    customer,
  };
}

export const AGREE_TO_USER_AGREEMENT = 'AGREE_TO_USER_AGREEMENT';

export function agreeToUserAgreement(user) {
  return dispatch => {
    dispatch({ type: AGREE_TO_USER_AGREEMENT, user });
    return apiClient
      .post(`/users/${user}/agreetouserterms/`, {})
      .then(checkHttpStatus)
      .then(response => normalize(response.data, userSchema))
      .then(json => {
        dispatch(
          addToast({
            title: 'Success!',
            message: 'User Agreement Accepted',
            level: 'success',
            dismissible: true,
            autoDismiss: 5,
          }),
        );
        return dispatch(receiveUserOptions(json.entities.userOptions));
      })
      .catch(error => dispatch(genericErrorAction(error, AGREE_TO_USER_AGREEMENT)));
  };
}

export const ACKNOWLEDGE_DISABLED_EMAIL_SETTINGS = 'ACKNOWLEDGE_DISABLED_EMAIL_SETTINGS';

export function acknowledgeDisabledEmailSettings(userOptionId) {
  return dispatch => {
    dispatch({ type: ACKNOWLEDGE_DISABLED_EMAIL_SETTINGS, userOptionId });
    return apiClient
      .patch(`/useroptions/${userOptionId}/`, { autodisabled_emails_timestamp: null })
      .then(checkHttpStatus)
      .then(response => normalize(response.data, userOptionSchema))
      .then(json => dispatch(receiveUserOptions(json.entities.userOptions)))
      .catch(error => dispatch(genericErrorAction(error, ACKNOWLEDGE_DISABLED_EMAIL_SETTINGS)));
  };
}
